/** @format */
import { Button } from '@mpb-com/feather/components/atoms/button';
import {
    LogoVariant,
    MpbLogo,
    MpbLogoSize,
} from '@mpb-com/feather/components/atoms/mpb-logo';
import { MediaQuery } from '@mpb-com/feather/helpers/media-queries';
import Script from 'next/script';
import React, {
    FunctionComponent,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { CookieConsentCategories } from '@toucan/config/cookie-consent';

import { useOperations } from '@toucan/client/blocks/hooks/use-operations';
import { ApplicationContext } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { Link, LinkTarget } from '@toucan/client/shared/components/link';
import { useMediaQuery } from '@toucan/client/shared/hooks/use-media-query';

import { styles } from './tableau-dashboard.styles';
import { HTMLTableauVizElement, TableauDashboardProps } from './types';

const StyledTableauDashboard = styled('div')`
    ${styles}
`;

const supportedLanguages = {
    en: 'English',
    fr: 'French',
    de: 'German',
    nl: 'Dutch',
    it: 'Italian',
    es: 'Spanish',
    pt: 'Portuguese',
};

export const TableauDashboard: FunctionComponent<TableauDashboardProps> = ({
    src,
}: TableauDashboardProps) => {
    const { legalLinks } = useContext(ApplicationContext);
    const { activeMarket } = useContext(MarketContext);
    const cookiePolicyLink = `/${activeMarket.slug}/${legalLinks?.cookiePage?.urlSlug}`;
    const [tableauScriptEnabled, setTableauScriptEnabled] = useState(false);
    const [functionalCookiesAccepted, setFunctionalCookiesAccepted] =
        useState(false);
    const [isWorkbookLoaded, setIsWorkbookLoaded] = useState(false);

    const tableauViz = useRef<HTMLTableauVizElement>(null);

    const { data: operationsData } = useOperations();

    const { operation } = operationsData ?? {};

    const defaultRegion = operation?.name;
    const defaultLanguage = supportedLanguages[activeMarket.language];

    const isTablet = useMediaQuery(MediaQuery.LARGE_TABLET);
    const isMobile = useMediaQuery(MediaQuery.MOBILE);

    const device = isMobile ? 'phone' : isTablet ? 'tablet' : 'desktop'; // eslint-disable-line no-nested-ternary

    // Onetrust event handler for consent being accepted elsewhere (e.g. main cookie banner)
    window?.OneTrust?.OnConsentChanged?.(checkConsentAndSetCookieState);

    function checkConsentAndSetCookieState() {
        if (
            window?.OnetrustActiveGroups?.includes(
                `,${CookieConsentCategories.FUNCTIONAL},`
            )
        ) {
            setTableauScriptEnabled(true);
            setFunctionalCookiesAccepted(true);
        }
    }

    function acceptCookieHandler() {
        if (window?.OneTrust?.UpdateConsent) {
            window?.OneTrust?.UpdateConsent?.(
                'Category',
                `${CookieConsentCategories.FUNCTIONAL}:1`
            );
            return;
        }

        setTableauScriptEnabled(true);
        setFunctionalCookiesAccepted(true);
    }

    async function setRegionInWorkbook(region: string) {
        await tableauViz.current?.workbook?.changeParameterValueAsync?.(
            'Region',
            region
        );
    }

    function setWorkbookAsLoaded() {
        setIsWorkbookLoaded(true);
    }

    useEffect(() => {
        if (defaultRegion && isWorkbookLoaded) {
            setRegionInWorkbook(defaultRegion);
        }
    }, [defaultRegion, isWorkbookLoaded]);

    useEffect(() => {
        if (!tableauViz.current) {
            return;
        }

        const tableauVizElement = tableauViz.current;

        tableauVizElement.addEventListener(
            'firstinteractive',
            setWorkbookAsLoaded
        );

        return () => {
            tableauVizElement.removeEventListener(
                'firstinteractive',
                setWorkbookAsLoaded
            );
        };
    }, [tableauScriptEnabled, functionalCookiesAccepted]);

    useEffect(() => {
        checkConsentAndSetCookieState();
    }, []);

    return (
        <StyledTableauDashboard>
            {tableauScriptEnabled && (
                <Script
                    type="module"
                    data-testid="tableau-dashboard__embed-script"
                    key="tableau-dashboard-embed-script"
                    src="https://public.tableau.com/javascripts/api/tableau.embedding.3.latest.min.js"
                />
            )}

            {!functionalCookiesAccepted && (
                <div className="tableau-dashboard-cookie-notice-container">
                    <div className="mpb-logo">
                        <MpbLogo
                            variant={LogoVariant.MONO}
                            logoSize={MpbLogoSize.LARGE}
                        />
                        Please accept cookies to view
                    </div>
                    <div className="tableau-dashboard-cookie-notice-banner">
                        <p>
                            By loading this chart you agree to accept{' '}
                            <Link
                                href={cookiePolicyLink}
                                target={LinkTarget.BLANK}
                            >
                                MPB cookie policy
                            </Link>{' '}
                            , this includes Functional cookies
                        </p>
                        <Button
                            onClick={acceptCookieHandler}
                            data-testid="tableau-dashboard__accept-cookies-button"
                        >
                            Accept cookie & load chart
                        </Button>
                    </div>
                </div>
            )}

            {tableauScriptEnabled && functionalCookiesAccepted && (
                <tableau-viz
                    id="tableauViz"
                    ref={tableauViz}
                    data-testid="tableau-dashboard__web-component"
                    src={src}
                    width="100%"
                    device={device}
                >
                    <viz-parameter
                        data-testid="tableau-dashboard__market-parameter"
                        name="[Parameters].[Parameter 2]"
                        value={defaultRegion}
                    ></viz-parameter>
                    <viz-parameter
                        data-testid="tableau-dashboard__language-parameter"
                        name="[Parameters].[Parameter 4]"
                        value={defaultLanguage}
                    ></viz-parameter>
                </tableau-viz>
            )}
        </StyledTableauDashboard>
    );
};
